import { Pipe, PipeTransform } from '@angular/core';
import { ChatServiceType } from '@nimbus-global/features/chat/models/chat.model';

export type ChatInlineNodeType = 'text' | 'bold' | 'italic' | 'strikethrough' | 'code' | 'monospace' | 'link';

export interface IChatInlineNode {
  type: ChatInlineNodeType;
  value: string;
  href?: string;
  // Present only for nested rich-text nodes (bold/italic/strikethrough).
  children?: IChatInlineNode[];
}

export type ChatBlockType = 'paragraph' | 'ordered-list' | 'unordered-list' | 'quote';

export interface IChatFormatBlock {
  type: ChatBlockType;
  lines: IChatInlineNode[][];
  orderedMarkers?: number[];
}

type ToggleFormat = 'bold' | 'italic' | 'strikethrough';
type ToggleRole = { kind: 'open' | 'close'; format: ToggleFormat } | null;

interface ILinkMatch {
  start: number;
  end: number;
  label: string;
  href: string;
}

interface ILinkPattern {
  regex: RegExp;
  normalizeHref: (match: string) => string;
  validate?: (match: string) => boolean;
}

interface IExtractedMonospace {
  text: string;
  tokens: Map<string, string>;
}

interface IInlineLiteralToken {
  type: 'code';
  value: string;
}

const TOGGLE_ORDER: ToggleFormat[] = ['bold', 'italic', 'strikethrough'];
const TOGGLE_FORMAT_BY_CHAR: Record<string, ToggleFormat> = { '*': 'bold', _: 'italic', '~': 'strikethrough' };
const ALNUM_PATTERN = /[\p{L}\p{N}]/u;
const WHITESPACE_PATTERN = /\s/;
const MIN_HOSTNAME_PARTS = 2;
const MIN_TLD_LENGTH = 2;

@Pipe({ name: 'chatFormat', standalone: true, pure: true })
export class ChatFormatPipe implements PipeTransform {
  private readonly orderedContentMatchIndex = 2;

  private readonly ORDERED_LIST_LINE = /^(\d+)\.\s+(.*)$/;
  private readonly UNORDERED_LIST_LINE = /^[-*]\s+(.*)$/;
  private readonly QUOTE_LINE = /^>\s?(.*)$/;

  // Inline code is literal, non-nesting. Triple-backtick monospace (including multiline) is extracted
  // earlier in parseWhatsapp so it survives line splitting and is restored as literal inline nodes here.
  private readonly CODE_FORMAT_SOURCE = '`(?<code>[^`\\n]+?)`';
  private readonly MONOSPACE_BLOCK = /```([\s\S]+?)```/g;
  private readonly MONOSPACE_TOKEN = /__NIMBUS_MONOSPACE_(\d+)__/g;
  private readonly INLINE_LITERAL_TOKEN = /@@NIMBUS_INLINE_LITERAL_(\d+)@@/g;

  private readonly TRAILING_PUNCTUATION = /^(.*?)([.,;:!?)*_~]*)$/;

  // Link detection (formerly the separate HighlightTokensPipe) - inlined here since this is its only consumer.
  private readonly LINK_PATTERNS: ILinkPattern[] = [
    {
      // email@domain.tld
      regex: /\b[a-z0-9._%+-]+@[a-z0-9-]+(?:\.[a-z0-9-]+)+\b/i,
      normalizeHref: (match) => `mailto:${match}`,
      validate: (match) => this.isValidEmail(match),
    },
    {
      // http://... or https://... for public hosts only
      regex: /\bhttps?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)]/i,
      normalizeHref: (match) => match,
      validate: (match) => this.isValidPublicHttpUrl(match),
    },
    {
      // www.domain.tld with optional path
      regex: /\bwww\.[a-z0-9-]+(?:\.[a-z0-9-]+)+(?:\/[^\s<>"']*[^\s<>"'.,;:!?)]|\/|)/i,
      normalizeHref: (match) => `https://${match}`,
      validate: (match) => this.isValidPublicHostname(match.split('/')[0]),
    },
    {
      // naked domain (no scheme)
      regex: /(?<![@\w.-])([a-z0-9-]+(?:\.[a-z0-9-]+)+)(?:\/[^\s<>"']*[^\s<>"'.,;:!?)]|\/|)/i,
      normalizeHref: (match) => `https://${match}`,
      validate: (match) => this.isValidNakedDomain(match),
    },
  ];

  public transform(text: string | null | undefined, channel?: ChatServiceType): IChatFormatBlock[] {
    if (!text) {
      return [];
    }

    return channel !== ChatServiceType.Whatsapp ? this.parsePlain(text) : this.parseWhatsapp(text);
  }

  // Plain mode: skip list/quote/toggle-format detection entirely, but keep link tokenization so URLs typed
  // into a non-formatting-capable channel (e.g. SMS, RCS) are still rendered as clickable links.
  private parsePlain(text: string): IChatFormatBlock[] {
    const nodes: IChatInlineNode[] = [];
    this.pushLinkAwareText(nodes, text);
    return [{ type: 'paragraph', lines: [nodes.length ? nodes : [{ type: 'text', value: '' }]] }];
  }

  private parseWhatsapp(text: string): IChatFormatBlock[] {
    const blocks: IChatFormatBlock[] = [];
    const extractedMonospace = this.extractMonospaceBlocks(text);

    for (const line of extractedMonospace.text.split('\n')) {
      const { type, content, orderedMarker } = this.matchLine(line);
      const nodes = this.parseInline(content, extractedMonospace.tokens);
      const lastBlock = blocks[blocks.length - 1];

      if (lastBlock?.type === type) {
        lastBlock.lines.push(nodes);
        if (type === 'ordered-list' && orderedMarker !== undefined) {
          (lastBlock.orderedMarkers ??= []).push(orderedMarker);
        }
      } else {
        blocks.push({
          type,
          lines: [nodes],
          orderedMarkers: type === 'ordered-list' && orderedMarker !== undefined ? [orderedMarker] : undefined,
        });
      }
    }

    return blocks;
  }

  private matchLine(line: string): { type: ChatBlockType; content: string; orderedMarker?: number } {
    const orderedMatch = line.match(this.ORDERED_LIST_LINE);
    if (orderedMatch) {
      return {
        type: 'ordered-list',
        content: orderedMatch[this.orderedContentMatchIndex],
        orderedMarker: parseInt(orderedMatch[1], 10),
      };
    }

    const unorderedMatch = line.match(this.UNORDERED_LIST_LINE);
    if (unorderedMatch) {
      return { type: 'unordered-list', content: unorderedMatch[1] };
    }

    const quoteMatch = line.match(this.QUOTE_LINE);
    if (quoteMatch) {
      return { type: 'quote', content: quoteMatch[1] };
    }

    return { type: 'paragraph', content: line };
  }

  private parseInline(text: string, monospaceTokens?: ReadonlyMap<string, string>): IChatInlineNode[] {
    if (!monospaceTokens?.size) {
      return this.parseInlineWithoutMonospaceTokens(text);
    }

    const nodes: IChatInlineNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    this.MONOSPACE_TOKEN.lastIndex = 0;
    while ((match = this.MONOSPACE_TOKEN.exec(text))) {
      const monospaceToken = match[0];
      const monospaceValue = monospaceTokens.get(monospaceToken);
      if (monospaceValue === undefined) {
        continue;
      }

      if (match.index > lastIndex) {
        nodes.push(...this.parseInlineWithoutMonospaceTokens(text.slice(lastIndex, match.index)));
      }

      nodes.push({ type: 'monospace', value: monospaceValue });
      lastIndex = match.index + monospaceToken.length;
    }

    if (lastIndex < text.length) {
      nodes.push(...this.parseInlineWithoutMonospaceTokens(text.slice(lastIndex)));
    }

    return nodes.length ? nodes : [{ type: 'text', value: '' }];
  }

  private parseInlineWithoutMonospaceTokens(text: string): IChatInlineNode[] {
    const extractedCode = this.extractInlineCodeTokens(text);
    return this.parseToggleSegment(extractedCode.text, extractedCode.tokens);
  }

  private extractMonospaceBlocks(text: string): IExtractedMonospace {
    const tokens = new Map<string, string>();
    let tokenIndex = 0;
    const replacedText = text.replace(this.MONOSPACE_BLOCK, (_match: string, value: string) => {
      let token = `__NIMBUS_MONOSPACE_${tokenIndex}__`;
      while (text.includes(token)) {
        tokenIndex++;
        token = `__NIMBUS_MONOSPACE_${tokenIndex}__`;
      }
      tokenIndex++;
      tokens.set(token, value);
      return token;
    });

    return { text: replacedText, tokens };
  }

  /**
   * Each of `*` `_` `~` independently toggles bold/italic/strikethrough on or off, rather than requiring a
   * strictly-nested matching pair - this mirrors WhatsApp's own behavior, where "crossed" markers like
   * `*_word*_` (bold closes before italic does) still combine, not just properly-nested ones like `*_word_*`.
   * A marker only toggles when it sits at a word boundary (not glued to a letter/digit) and has real,
   * non-whitespace-adjacent content to open or close around; otherwise (including a marker left open at the
   * end of the text, which never validly closed) it's left as literal text.
   */
  private parseToggleSegment(text: string, inlineLiteralTokens?: ReadonlyMap<string, IInlineLiteralToken>): IChatInlineNode[] {
    const roles = this.decideToggleRoles(text);

    const active: Record<ToggleFormat, boolean> = { bold: false, italic: false, strikethrough: false };
    const runs: Array<{ styles: ToggleFormat[]; text: string }> = [];
    let currentText = '';

    const activeStyles = (): ToggleFormat[] => TOGGLE_ORDER.filter((format) => active[format]);
    const flush = (): void => {
      if (currentText.length > 0) {
        runs.push({ styles: activeStyles(), text: currentText });
      }
      currentText = '';
    };

    for (let i = 0; i < text.length; i++) {
      const role = roles[i];
      if (!role) {
        currentText += text[i];
        continue;
      }
      flush();
      active[role.format] = role.kind === 'open';
    }
    flush();

    const nodes: IChatInlineNode[] = [];
    for (const run of runs) {
      const leafNodes = this.buildLeafNodes(run.text, inlineLiteralTokens);
      for (const leaf of leafNodes) {
        nodes.push(this.wrapWithStyles(leaf, run.styles));
      }
    }
    return nodes.length ? nodes : [{ type: 'text', value: '' }];
  }

  // First pass: decide which marker characters genuinely open/close a format. A format left open at the
  // end of the text never validly closed, so its opening marker is retroactively cleared back to a plain
  // (literal) character - an unclosed marker must not silently format the remainder of the message.
  private decideToggleRoles(text: string): ToggleRole[] {
    const roles: ToggleRole[] = new Array(text.length).fill(null);
    const active: Record<ToggleFormat, boolean> = { bold: false, italic: false, strikethrough: false };
    const openIndex: Partial<Record<ToggleFormat, number>> = {};
    const lastOpenTokenIndex: Partial<Record<ToggleFormat, number>> = {};

    for (let i = 0; i < text.length; i++) {
      const format = TOGGLE_FORMAT_BY_CHAR[text[i]];
      if (!format) {
        continue;
      }

      if (!active[format]) {
        const prevChar = text[i - 1];
        const nextChar = text[i + 1];
        const canOpen = !ALNUM_PATTERN.test(prevChar ?? '') && !!nextChar && !WHITESPACE_PATTERN.test(nextChar) && nextChar !== text[i];
        if (canOpen) {
          roles[i] = { kind: 'open', format };
          active[format] = true;
          openIndex[format] = i;
          lastOpenTokenIndex[format] = i;
        }
        continue;
      }

      const prevChar = text[i - 1];
      const nextChar = text[i + 1];
      const hasContent = i > (openIndex[format] as number) + 1;
      const canClose = hasContent && !WHITESPACE_PATTERN.test(prevChar ?? '') && !ALNUM_PATTERN.test(nextChar ?? '');
      if (canClose) {
        roles[i] = { kind: 'close', format };
        active[format] = false;
      }
    }

    for (const format of TOGGLE_ORDER) {
      if (active[format]) {
        roles[lastOpenTokenIndex[format] as number] = null;
      }
    }

    return roles;
  }

  private wrapWithStyles(leaf: IChatInlineNode, styles: ToggleFormat[]): IChatInlineNode {
    return styles.reduceRight<IChatInlineNode>((child, style) => ({ type: style, value: child.value, children: [child] }), leaf);
  }

  private buildLeafNodes(text: string, inlineLiteralTokens?: ReadonlyMap<string, IInlineLiteralToken>): IChatInlineNode[] {
    if (!inlineLiteralTokens?.size) {
      const nodes: IChatInlineNode[] = [];
      this.pushLinkAwareText(nodes, text);
      return nodes;
    }

    const nodes: IChatInlineNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    this.INLINE_LITERAL_TOKEN.lastIndex = 0;
    while ((match = this.INLINE_LITERAL_TOKEN.exec(text))) {
      const token = match[0];
      const inlineLiteral = inlineLiteralTokens.get(token);
      if (!inlineLiteral) {
        continue;
      }

      if (match.index > lastIndex) {
        this.pushLinkAwareText(nodes, text.slice(lastIndex, match.index));
      }

      nodes.push({ type: inlineLiteral.type, value: inlineLiteral.value });
      lastIndex = match.index + token.length;
    }

    if (lastIndex < text.length) {
      this.pushLinkAwareText(nodes, text.slice(lastIndex));
    }

    return nodes;
  }

  private extractInlineCodeTokens(text: string): { text: string; tokens: Map<string, IInlineLiteralToken> } {
    const regex = new RegExp(this.CODE_FORMAT_SOURCE, 'g');
    const tokens = new Map<string, IInlineLiteralToken>();
    let tokenIndex = 0;
    const replacedText = text.replace(regex, (_match: string, code: string) => {
      let token = `@@NIMBUS_INLINE_LITERAL_${tokenIndex}@@`;
      tokenIndex++;
      while (text.includes(token)) {
        token = `@@NIMBUS_INLINE_LITERAL_${tokenIndex}@@`;
        tokenIndex++;
      }
      tokens.set(token, { type: 'code', value: code });
      return token;
    });

    return { text: replacedText, tokens };
  }

  private pushLinkAwareText(nodes: IChatInlineNode[], value: string): void {
    let position = 0;

    while (position < value.length) {
      const link = this.findNextLink(value, position);

      if (!link) {
        if (position < value.length) {
          nodes.push({ type: 'text', value: value.slice(position) });
        }
        return;
      }

      if (link.start > position) {
        nodes.push({ type: 'text', value: value.slice(position, link.start) });
      }

      nodes.push({ type: 'link', value: link.label, href: link.href });
      position = link.end;
    }
  }

  private findNextLink(text: string, fromIndex: number): ILinkMatch | null {
    const candidates = this.LINK_PATTERNS.map((pattern) => this.tryMatchPattern(text, fromIndex, pattern)).filter(
      (match): match is ILinkMatch => !!match,
    );

    if (candidates.length === 0) {
      return null;
    }

    // Return earliest match (or longest if same position)
    return candidates.sort((a, b) => (a.start !== b.start ? a.start - b.start : b.end - a.end))[0];
  }

  private tryMatchPattern(text: string, fromIndex: number, pattern: ILinkPattern): ILinkMatch | null {
    const slice = text.slice(fromIndex);
    const regex = new RegExp(pattern.regex.source, this.withGlobalFlag(pattern.regex.flags));
    let match = regex.exec(slice);

    while (match) {
      const rawMatch = match[0];
      const localStart = match.index;
      const { label, trailingLength } = this.trimTrailingPunctuation(rawMatch);

      // Skip invalid matches and keep scanning for the next valid candidate.
      if (!pattern.validate || pattern.validate(label)) {
        return {
          start: fromIndex + localStart,
          end: fromIndex + localStart + rawMatch.length - trailingLength,
          label,
          href: pattern.normalizeHref(label),
        };
      }

      match = regex.exec(slice);
    }

    return null;
  }

  private trimTrailingPunctuation(text: string): { label: string; trailingLength: number } {
    const match = text.match(this.TRAILING_PUNCTUATION);
    if (!match) {
      return { label: text, trailingLength: 0 };
    }
    return {
      label: match[1],
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      trailingLength: match[2].length,
    };
  }

  private isValidNakedDomain(domain: string): boolean {
    return this.isValidPublicHostname(domain.split('/')[0]);
  }

  private isValidPublicHttpUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
        return false;
      }
      return this.isValidPublicHostname(parsedUrl.hostname);
    } catch {
      return false;
    }
  }

  private isValidEmail(email: string): boolean {
    const [localPart, domainPart] = email.split('@');
    if (!localPart || !domainPart) {
      return false;
    }
    return this.isValidPublicHostname(domainPart);
  }

  private isValidPublicHostname(hostname: string): boolean {
    const normalizedHostname = hostname.toLowerCase();

    if (normalizedHostname === 'localhost' || normalizedHostname === '::1') {
      return false;
    }
    if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(normalizedHostname)) {
      return false;
    }

    const parts = normalizedHostname.split('.');
    const tld = parts[parts.length - 1];

    return (
      parts.length >= MIN_HOSTNAME_PARTS &&
      /^(?=.*[a-z])[a-z0-9-]+$/i.test(tld) &&
      tld.length >= MIN_TLD_LENGTH &&
      /^[a-z0-9.-]+$/i.test(normalizedHostname)
    );
  }

  private withGlobalFlag(flags: string): string {
    return flags.includes('g') ? flags : `${flags}g`;
  }
}
